"use client";

import { useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";

import TopupSection from "@/features/landing/components/TopupSection";
import SelectedProductSummary from "@/components/SelectedProductSummary";

import { useAuth } from "@/context/AuthContext";
import { usePiSocketCtx } from "@/context/PiSocketContext";
import { fetchBrandsMap } from "@/utils/api/brands";
import { isBlocked } from "@/features/landing/utils/blocklist";

export default function TopupsPageClient({ topupInitial }) {
  const { user } = useAuth();
  const { piRate, basePI, totalPI, feePercent, requestPriceUpdate } = usePiSocketCtx();

  const [brands, setBrands] = useState({});
  const [selectedProduct, _setSelectedProduct] = useState(null);

  const setSelectedProduct = (p) => {
    if (p && isBlocked(p)) {
      toast.warn("This supplier is temporarily unavailable.");
      return;
    }
    _setSelectedProduct(p);
  };

  useEffect(() => {
    document.body.style.overflow = selectedProduct ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [selectedProduct]);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const map = await fetchBrandsMap();
        if (alive) setBrands(map || {});
      } catch (e) {
        console.error("Failed to load brands map:", e);
      }
    })();
    return () => { alive = false; };
  }, []);

  const priceUSD = useMemo(() => {
    return selectedProduct?.price?.fixed ? selectedProduct.price.fixed / 100 : "N/A";
  }, [selectedProduct]);

  useEffect(() => {
    if (!selectedProduct?.offerId) return;
    const usd = Number(priceUSD);
    if (!Number.isFinite(usd) || usd <= 0) return;
    requestPriceUpdate(usd, selectedProduct.offerId);
  }, [priceUSD, selectedProduct?.offerId, requestPriceUpdate]);

  return (
    <>
      <TopupSection
        initial={topupInitial}
        brands={brands}
        selectedProduct={selectedProduct}
        onSelect={setSelectedProduct}
        piRate={piRate}
        feePercent={feePercent}
        productFilter={(p) => !isBlocked(p)}
      />

      {selectedProduct && (
        <div className="fixed inset-0 z-50 flex items-center justify-end">
          <div
            className="absolute inset-0 bg-black/50 backdrop-blur-sm"
            onClick={() => setSelectedProduct(null)}
          />
          <div
            className="relative w-full max-w-md bg-surface text-textPrimary p-4 h-full overflow-y-auto
                       border-l border-border shadow-xl"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              onClick={(e) => {
                e.stopPropagation();
                setSelectedProduct(null);
              }}
              className="absolute top-4 right-4 text-xl font-bold text-textSecondary hover:text-accent"
              aria-label="Close"
              title="Close"
            >
              &times;
            </button>

            <SelectedProductSummary
              product={selectedProduct}
              brands={brands}
              priceUSD={priceUSD}
              piRate={piRate}
              basePI={basePI}
              totalPI={totalPI}
              feePercent={feePercent}
              user={user}
              onCancel={() => setSelectedProduct(null)}
            />
          </div>
        </div>
      )}
    </>
  );
}
